import { client } from '~publish/legacy/apollo-client'
import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'

import type { HashtagGroup } from './types'

/**
 * Account-actor mutation that replaces the legacy `updateHashtagGroup` RPC /
 * `POST /1/snippet_groups/update.json` for Publish Hashtag Manager.
 * Backend ships as Snippet GraphQL (CHAN-12108), not HashtagGroup naming.
 */
export const UpdateSnippet = graphql(/* GraphQL */ `
  mutation UpdateSnippet($input: UpdateSnippetInput!) {
    updateSnippet(input: $input) {
      __typename
      ... on UpdateSnippetSuccess {
        snippet {
          id
          name
          text
        }
      }
      ... on VoidMutationError {
        message
      }
    }
  }
`)

type UpdateSnippetPayload = NonNullable<
  ResultOf<typeof UpdateSnippet>['updateSnippet']
>

export function mapSnippetToHashtagGroup(
  snippet: Extract<
    UpdateSnippetPayload,
    { __typename: 'UpdateSnippetSuccess' }
  >['snippet'],
): HashtagGroup {
  return {
    _id: snippet.id,
    id: snippet.id,
    name: snippet.name,
    text: snippet.text,
  }
}

export async function updateHashtagGroupViaGraphQL(args: {
  organizationId: string
  snippetId: string
  name: string
  text: string
}): Promise<HashtagGroup> {
  const { data } = await client.mutate({
    mutation: UpdateSnippet,
    variables: {
      input: {
        organizationId: args.organizationId,
        id: args.snippetId,
        name: args.name,
        text: args.text,
      },
    },
  })

  const payload = data?.updateSnippet
  if (!payload) {
    throw new Error('UpdateSnippet returned no payload')
  }
  if (payload.__typename === 'VoidMutationError') {
    throw new Error(payload.message)
  }
  if (payload.__typename !== 'UpdateSnippetSuccess') {
    throw new Error('UpdateSnippet returned an unexpected payload')
  }

  return mapSnippetToHashtagGroup(payload.snippet)
}
